import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Store, AlertTriangle } from "lucide-react";

export default function MarkCreditDialog({ open, onOpenChange, customer, onDone }) {
  const [amount, setAmount] = useState("");
  const [shopId, setShopId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [shops, setShops] = useState([]);

  useEffect(() => {
    if (open) {
      setAmount("");
      setShopId("");
      setReason("");
      // Load shops
      api.get("/shops").then(({ data }) => {
        setShops(data.items || []);
        if (data.items?.length === 1) {
          setShopId(data.items[0].id);
        }
      }).catch(() => setShops([]));
    }
  }, [open]);

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (!shopId) return toast.error("Please select a shop");
    if (!reason.trim()) return toast.error("Please enter a reason");
    setBusy(true);
    try {
      await api.post(`/credit/${customer.id}/mark-credit`, { 
        amount: amt, 
        shop_id: shopId, 
        reason: reason.trim() 
      });
      toast.success(`Credit of ₹${amt.toFixed(2)} marked for ${customer.name}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="mark-credit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Mark Credit (Customer Owes)
          </DialogTitle>
          <DialogDescription>
            Record that <span className="font-medium">{customer?.name}</span> owes money for goods/services without a formal sale.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
            <strong>Note:</strong> This creates a receivable entry. Use this for special cases like goods taken without billing, services rendered, or adjustments.
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1">
              <Store className="w-3 h-3" /> Shop *
            </Label>
            <Select value={shopId} onValueChange={setShopId}>
              <SelectTrigger className="mt-1" data-testid="mark-credit-shop-select">
                <SelectValue placeholder="Select shop" />
              </SelectTrigger>
              <SelectContent>
                {shops.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Amount *</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 font-mono-data"
              placeholder="0.00"
              data-testid="mark-credit-amount-input"
            />
          </div>
          <div>
            <Label className="text-xs">Reason / Description *</Label>
            <Textarea 
              value={reason} 
              onChange={(e) => setReason(e.target.value)} 
              className="mt-1" 
              rows={2} 
              placeholder="E.g., Goods taken on credit, Service charge, etc."
              data-testid="mark-credit-reason-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button 
            onClick={submit} 
            disabled={busy || !amount || !shopId || !reason.trim()} 
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="mark-credit-submit"
          >
            {busy ? "Saving…" : "Mark Credit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
